<template>
  <div v-if="shouldRender" class="td-booking-chat">
    <button
      v-if="!panelOpen"
      class="td-booking-chat__launcher"
      :style="launcherStyle"
      type="button"
      @click="openPanel"
    >
      <span class="td-booking-chat__dot" :style="accentStyle"></span>
      <span>{{ copy.launcher }}</span>
    </button>

    <section
      v-else
      class="td-booking-chat__panel"
      :class="{ dragging: dragActive }"
      aria-live="polite"
      @dragover.prevent="dragActive = true"
      @dragleave.prevent="dragActive = false"
      @drop.prevent="handleDrop"
      @paste="handlePaste"
    >
      <header class="td-booking-chat__header" :style="headerStyle">
        <div>
          <strong>{{ copy.title }}</strong>
          <span>{{ copy.subtitle }}</span>
        </div>
        <button type="button" class="td-booking-chat__close" @click="closePanel" aria-label="Close chat">x</button>
      </header>

      <div class="td-booking-chat__body">
        <div v-if="errorMessage" class="td-booking-chat__alert">{{ errorMessage }}</div>
        <div v-if="offlineMode && !sessionToken" class="td-booking-chat__offline">{{ copy.offlineNotice }}</div>

        <template v-if="!sessionToken">
          <p class="td-booking-chat__intro">{{ offlineMode ? copy.offlineIntro : copy.intro }}</p>
          <label>
            {{ copy.nameLabel }}
            <input v-model.trim="guest.name" type="text" :placeholder="copy.namePlaceholder">
          </label>
          <label>
            {{ copy.phoneLabel }}
            <PhoneInputComponent v-model="guest.phone" :placeholder="copy.phonePlaceholder" />
          </label>
          <label>
            {{ copy.emailLabel }}
            <input v-model.trim="guest.email" type="email" :placeholder="copy.emailPlaceholder">
          </label>
          <label>
            {{ copy.messageLabel }}
            <textarea
              v-model.trim="draft"
              rows="4"
              :placeholder="copy.messagePlaceholder"
              @input="handleTypingInput"
            @focus="handleTypingFocus"
              @blur="handleTypingBlur"
            ></textarea>
          </label>
        </template>

        <template v-else>
          <div ref="messagesBox" class="td-booking-chat__messages">
            <div v-if="loadingMessages && messages.length === 0" class="td-booking-chat__skeletons" aria-hidden="true">
              <span v-for="item in 4" :key="`booking-widget-skeleton-${item}`" :class="{ guest: item % 2 === 0 }"></span>
            </div>

            <div
              v-for="message in messages"
              :key="message.id"
              class="td-booking-chat__message-row"
              :class="{ guest: message.sender_type === 'guest' }"
            >
              <span class="td-booking-chat__avatar" :class="{ guest: message.sender_type === 'guest' }">
                <img v-if="messageAvatar(message)" :src="messageAvatar(message)" alt="">
                <template v-else>{{ messageInitials(message) }}</template>
              </span>
              <div class="td-booking-chat__message">
                <div class="td-booking-chat__message-sender">{{ senderLabel(message) }}</div>
                <button
                  v-if="messageAttachment(message)"
                  type="button"
                  class="td-booking-chat__message-attachment"
                  @click="openImage(messageAttachment(message))"
                >
                  <img :src="messageAttachment(message).url || messageAttachment(message).preview_url" :alt="messageAttachment(message).name || 'Attachment'">
                </button>
                <div v-if="shouldShowBody(message)" class="td-booking-chat__message-body">{{ message.body }}</div>
                <small>
                  {{ formatTime(message.created_at) }}
                  <span v-if="message._status === 'sending'">{{ copy.sending }}</span>
                  <span v-else-if="message._status === 'failed'">{{ copy.failed }}</span>
                </small>
              </div>
            </div>

            <div v-if="agentTyping" class="td-booking-chat__typing">
              <span></span>{{ copy.agentTyping }}
            </div>
          </div>

          <div v-if="sessionClosed" class="td-booking-chat__closed"><strong>{{ copy.closedTitle }}</strong><span>{{ copy.closedText }}</span></div>

          <form v-if="sessionClosed && !ratingSubmitted" class="td-booking-chat__rating" @submit.prevent="submitRating">
            <strong>{{ copy.ratingTitle }}</strong>
            <div v-if="ratingError" class="td-booking-chat__alert">{{ ratingError }}</div>
            <div class="td-booking-chat__stars" role="radiogroup" :aria-label="copy.ratingTitle" @mouseleave="hoverRating = 0">
              <button
                v-for="star in 5"
                :key="star"
                type="button"
                class="td-booking-chat__star"
                role="radio"
                :aria-label="starLabel(star)"
                :aria-checked="displayRating === star"
                :style="{ '--star-fill': `${starFill(star)}%` }"
                @mousemove="previewRating($event, star)"
                @focus="hoverRating = rating || star"
                @click="setRatingFromPointer($event, star)"
              >
                <span class="td-booking-chat__star-base">★</span>
                <span class="td-booking-chat__star-fill">★</span>
              </button>
            </div>
            <textarea v-model.trim="ratingComment" rows="3" :placeholder="copy.ratingCommentPlaceholder"></textarea>
            <button class="td-booking-chat__send" :style="sendButtonStyle" type="submit" :disabled="ratingSending || !rating">{{ ratingSending ? copy.sending : copy.submitRating }}</button>
          </form>

          <div v-if="ratingSubmitted" class="td-booking-chat__thanks">{{ copy.ratingThanks }}</div>
          <button v-if="sessionClosed" type="button" class="td-booking-chat__new" @click="startNewConversation">{{ copy.newConversation }}</button>

          <label v-if="!sessionClosed" class="td-booking-chat__reply">
            {{ copy.replyLabel }}
            <textarea
              v-model.trim="draft"
              rows="3"
              :placeholder="copy.replyPlaceholder"
              @input="handleTypingInput"
              @focus="handleTypingFocus"
              @blur="handleTypingBlur"
            ></textarea>
          </label>
        </template>
      </div>

      <footer class="td-booking-chat__footer">
        <input ref="attachmentInput" class="td-booking-chat__file" type="file" accept="image/*" @change="handleFilePicked">
        <div v-if="attachment" class="td-booking-chat__attachment-preview">
          <img :src="attachmentPreviewUrl" alt="Selected image">
          <span>{{ attachment.name }}</span>
          <button type="button" @click="clearAttachment">x</button>
        </div>
        <div v-if="attachmentError" class="td-booking-chat__attachment-error">{{ attachmentError }}</div>
        <button v-if="sessionToken && !sessionClosed" type="button" class="td-booking-chat__end" @click="closeConversation">{{ copy.endConversation }}</button>
        <div v-if="!sessionClosed" class="td-booking-chat__actions">
          <button type="button" class="td-booking-chat__attach" :title="copy.attachImage" :aria-label="copy.attachImage" @click="openAttachmentPicker">
            <i class="fa-solid fa-paperclip" aria-hidden="true"></i>
          </button>
          <button type="button" class="td-booking-chat__attach" :title="copy.emoji" :aria-label="copy.emoji" @click="showEmojiPicker = !showEmojiPicker">
            <i class="fa-regular fa-face-smile" aria-hidden="true"></i>
          </button>
          <div v-if="showEmojiPicker" class="td-booking-chat__emoji-picker">
            <button v-for="emoji in emojiOptions" :key="emoji" type="button" @click="appendEmoji(emoji)">{{ emoji }}</button>
          </div>
          <button
            class="td-booking-chat__send"
            :style="sendButtonStyle"
            type="button"
            :disabled="sending || (!draft && !attachment)"
            @click="sendMessage"
          >
            {{ sending ? copy.sending : copy.send }}
          </button>
        </div>
      </footer>
    </section>

    <div v-if="lightboxImage" class="td-booking-chat__lightbox" @click.self="closeLightbox">
      <button type="button" class="td-booking-chat__lightbox-close" @click="closeLightbox">x</button>
      <img :src="lightboxImage.url" :alt="lightboxImage.name || 'Attachment'">
    </div>
  </div>
</template>

<script>
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import PhoneInputComponent from '@/global-components/PhoneInputComponent.vue';

window.Pusher = Pusher;

export default {
  name: 'BookingLiveChatWidget',
  components: {
    PhoneInputComponent,
  },
  data() {
    return {
      available: false,
      canCreateSession: true,
      availabilityReason: '',
      availabilityData: null,
      panelOpen: false,
      sending: false,
      loadingMessages: false,
      errorMessage: '',
      sessionToken: '',
      sessionStatus: 'open',
      subscribedToken: '',
      availabilitySubscribedCompany: '',
      echo: null,
      pusher: null,
      messages: [],
      draft: '',
      attachment: null,
      attachmentPreviewUrl: '',
      attachmentError: '',
      dragActive: false,
      agentTyping: false,
      agentTypingTimer: null,
      typingState: false,
      typingStopTimer: null,
      typingComposerFocused: false,
      bookingStepPoller: null,
      rating: 0,
      hoverRating: 0,
      ratingComment: '',
      ratingError: '',
      ratingSending: false,
      ratingSubmitted: false,
      lightboxImage: null,
      showEmojiPicker: false,
      notificationAudio: null,
      notificationAudioUnlocked: false,
      emojiOptions: ['\u{1F600}', '\u{1F60A}', '\u{1F44D}', '\u{1F64F}', '\u{1F64C}', '\u{1F697}', '\u{1F3DD}\u{FE0F}', '\u{1F37D}\u{FE0F}', '\u{2705}', '\u{2B50}', '\u{2764}\u{FE0F}', '\u{1F4CD}'],
      notifiedMessageIds: new Set(),
      handledEventKeys: new Set(),
      visitedPages: [],
      funnelSessionKey: '',
      lastFunnelVisitSignature: '',
      visitorStartedAt: new Date().toISOString(),
      guest: {
        name: '',
        phone: '',
        email: '',
      },
    };
  },
  computed: {
    company() {
      return this.$store.getters.getCompanyData?.data || null;
    },
    companyId() {
      return this.company?.company_id || null;
    },
    language() {
      return this.$store.getters.getBookingLanguage === 'es' ? 'es' : 'en';
    },
    currentStep() {
      return Number(this.$store.getters.getCurrentStep || this.$store.state.currentStep || 0);
    },
    activeServices() {
      const services = this.company?.company_active_services || [];

      if (Array.isArray(services)) {
        return services.map(Number);
      }

      if (typeof services === 'string') {
        try {
          const parsed = JSON.parse(services);
          return Array.isArray(parsed) ? parsed.map(Number) : [];
        } catch (error) {
          return services.split(',').map((service) => Number(service.trim())).filter(Boolean);
        }
      }

      return [];
    },
    industry() {
      if (!this.companyId) return '';

      if (this.$route.name === 'restaurant-booking-engine' || this.$route.name === 'public-restaurant-reservation-confirmation' || this.$route.path.startsWith('/restaurant')) {
        return this.activeServices.includes(3) ? 'restaurant' : '';
      }

      if (this.$route.name === 'tours-booking-engine' || this.$route.name === 'public-tour-reservation-confirmation' || this.$route.path.startsWith('/activities')) {
        return this.activeServices.includes(2) ? 'tours' : '';
      }

      if (this.$route.name === 'public-reservation-confirmation') {
        return this.activeServices.includes(1) ? 'transportation' : '';
      }

      if (this.activeServices.includes(1)) return 'transportation';
      if (this.activeServices.includes(2)) return 'tours';
      if (this.activeServices.includes(3)) return 'restaurant';

      return '';
    },
    shouldRender() {
      return Boolean(this.companyId && this.industry);
    },
    offlineMode() {
      return this.canCreateSession && !this.available;
    },
    sessionClosed() {
      return this.sessionStatus === 'closed';
    },
    storageKey() {
      return this.companyId && this.industry
        ? `traveldesk_booking_chat_${this.companyId}_${this.industry}`
        : '';
    },
    funnelStorageKey() {
      return this.companyId && this.industry
        ? `traveldesk_booking_funnel_${this.companyId}_${this.industry}`
        : '';
    },
    brandColor() {
      const color = (this.company?.company_color || '#2d1b78').trim();
      return /^#[0-9a-f]{3,8}$/i.test(color) ? color : '#2d1b78';
    },
    contrastColor() {
      const hex = this.brandColor.replace('#', '');
      const normalized = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex.slice(0, 6);
      const value = parseInt(normalized, 16);
      const r = (value >> 16) & 255;
      const g = (value >> 8) & 255;
      const b = value & 255;
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.62 ? '#111827' : '#ffffff';
    },
    launcherStyle() {
      return {
        backgroundColor: '#ffffff',
        borderColor: 'rgba(17, 24, 39, .16)',
        color: '#111827',
      };
    },
    accentStyle() {
      return {
        backgroundColor: this.available ? '#2fe49a' : '#f59e0b',
        boxShadow: `0 0 0 4px ${this.hexToRgba(this.brandColor, 0.18)}`,
      };
    },
    sendButtonStyle() {
      return {
        backgroundColor: this.brandColor,
        borderColor: this.brandColor,
        color: this.contrastColor,
      };
    },
    headerStyle() {
      return {
        borderTopColor: this.brandColor,
      };
    },
    industrySubtitle() {
      const labels = {
        transportation: {
          es: 'Soporte para tu traslado, horario y detalles de reserva',
          en: 'Support with your transfer, pickup time and booking details',
        },
        tours: {
          es: 'Soporte para actividades, horarios y disponibilidad',
          en: 'Support with activities, schedules and availability',
        },
        restaurant: {
          es: 'Soporte para tu mesa, horario y confirmación',
          en: 'Support with your table, time and reservation details',
        },
      };

      return labels[this.industry] || labels.transportation;
    },
    displayRating() {
      return Number(this.hoverRating || this.rating || 0);
    },
    copy() {
      const es = {
        launcher: '¿Necesitas ayuda?',
        title: '¿Necesitas ayuda con tu reserva?',
        subtitle: this.industrySubtitle.es,
        intro: 'Envianos tu pregunta y un agente de la compania te respondera aqui.',
        offlineNotice: 'No hay agentes en linea ahora. Deja tu mensaje y la compania lo vera al iniciar sesion.',
        offlineIntro: 'Dejanos tus datos y tu mensaje. Te responderemos en cuanto un agente este disponible.',
        nameLabel: 'Nombre',
        namePlaceholder: 'Tu nombre',
        phoneLabel: 'Telefono',
        phonePlaceholder: 'Opcional',
        emailLabel: 'Email',
        emailPlaceholder: 'Opcional',
        messageLabel: 'Mensaje',
        messagePlaceholder: 'Como podemos ayudarte?',
        replyLabel: 'Responder',
        replyPlaceholder: 'Escribe tu mensaje',
        send: 'Enviar',
        sending: 'Enviando...',
        failed: 'No enviado',
        guest: 'Tu',
        agent: 'Agente',
        agentTyping: 'El agente esta escribiendo...',
        attachImage: 'Adjuntar imagen',
        emoji: 'Emoji',
        imageOnly: 'Solo puedes adjuntar imagenes.',
        imageTooLarge: 'La imagen debe pesar 5 MB o menos.',
        imageMessage: 'Imagen',
        error: 'No pudimos enviar el mensaje. Intenta de nuevo.',
        closeError: 'No pudimos finalizar la conversacion. Intenta de nuevo.',
        endConversation: 'Finalizar conversacion',
        closedTitle: 'Conversacion finalizada',
        closedText: 'Gracias por contactarnos.',
        ratingTitle: 'Como fue tu experiencia?',
        ratingCommentPlaceholder: 'Comentario opcional',
        submitRating: 'Enviar calificacion',
        ratingThanks: 'Gracias por tu calificacion.',
        ratingError: 'No pudimos enviar tu calificacion. Intenta de nuevo.',
        newConversation: 'Iniciar nueva conversacion',
        browserTitle: 'TravelDesk LiveChat',
      };
      const en = {
        launcher: 'Need help?',
        title: 'Need help with your booking?',
        subtitle: this.industrySubtitle.en,
        intro: 'Send your question and a company agent will reply here.',
        offlineNotice: 'No agents are online right now. Leave a message and the company will see it when they sign in.',
        offlineIntro: 'Leave your details and message. An agent will reply as soon as they are available.',
        nameLabel: 'Name',
        namePlaceholder: 'Your name',
        phoneLabel: 'Phone',
        phonePlaceholder: 'Optional',
        emailLabel: 'Email',
        emailPlaceholder: 'Optional',
        messageLabel: 'Message',
        messagePlaceholder: 'How can we help?',
        replyLabel: 'Reply',
        replyPlaceholder: 'Write your message',
        send: 'Send',
        sending: 'Sending...',
        failed: 'Failed',
        guest: 'You',
        agent: 'Agent',
        agentTyping: 'Agent is typing...',
        attachImage: 'Attach image',
        emoji: 'Emoji',
        imageOnly: 'Only images can be attached.',
        imageTooLarge: 'Image must be 5 MB or less.',
        imageMessage: 'Image',
        error: 'We could not send the message. Try again.',
        closeError: 'We could not end the conversation. Try again.',
        endConversation: 'End conversation',
        closedTitle: 'Conversation ended',
        closedText: 'Thanks for contacting us.',
        ratingTitle: 'How was your experience?',
        ratingCommentPlaceholder: 'Optional comment',
        submitRating: 'Submit rating',
        ratingThanks: 'Thanks for your rating.',
        ratingError: 'We could not submit your rating. Try again.',
        newConversation: 'Start a new conversation',
        browserTitle: 'TravelDesk LiveChat',
      };

      return this.language === 'es' ? es : en;
    },
  },
  watch: {
    companyId: 'resetAndRefresh',
    industry: 'resetAndRefresh',
    currentStep: 'handleBookingStepChanged',
    '$route.fullPath': 'handleRouteChanged',
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.recordPageVisit('mount');
    this.bookingStepPoller = window.setInterval(this.trackCurrentBookingStep, 1500);
    this.resetAndRefresh();
  },
  beforeUnmount() {
    this.recordPageVisit('leave');
    this.stopOwnTyping({ force: true });
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.clearInterval(this.bookingStepPoller);
    this.unsubscribeAvailabilityChannel();
    this.unsubscribeSessionChannel();
    this.disconnectEcho();
    this.stopTypingTimers();
    this.clearAttachment();
  },
  methods: {
    apiUrl(path) {
      const base = (process.env.VUE_APP_BASEURL || '').replace(/\/+$/, '');
      return `${base}/${path.replace(/^\/+/, '')}`;
    },
    async responseMessage(response, fallback) {
      try {
        const payload = await response.clone().json();
        return payload?.message || payload?.error || fallback;
      } catch {
        return fallback;
      }
    },
    async resetAndRefresh() {
      this.stopOwnTyping({ force: true });
      this.available = false;
      this.funnelSessionKey = '';
      this.lastFunnelVisitSignature = '';
      this.unsubscribeSessionChannel();
      this.subscribeAvailabilityChannel();
      this.sessionToken = this.restoreSessionToken();
      this.messages = [];
      this.sessionStatus = 'open';
      this.rating = 0;
      this.hoverRating = 0;
      this.ratingComment = '';
      this.ratingError = '';
      this.ratingSubmitted = false;
      await this.refreshAvailability();
      if (this.sessionToken) {
        await this.loadMessages();
        this.subscribeSessionChannel();
      }
    },
    handleRouteChanged() {
      this.recordPageVisit('route');
      this.refreshAvailability();
    },
    handleBookingStepChanged() {
      this.trackCurrentBookingStep();
    },
    restoreSessionToken() {
      if (!this.storageKey) return '';
      return window.localStorage.getItem(this.storageKey) || '';
    },
    persistSessionToken(token) {
      if (!this.storageKey) return;
      if (token) window.localStorage.setItem(this.storageKey, token);
      else window.localStorage.removeItem(this.storageKey);
    },
    async refreshAvailability() {
      if (!this.companyId || !this.industry) {
        this.available = false;
        this.canCreateSession = false;
        return;
      }

      try {
        const params = new URLSearchParams({
          company_id: String(this.companyId),
          industry: this.industry,
        });
        const response = await fetch(this.apiUrl(`public/booking-chat/availability?${params.toString()}`));
        const data = await response.json();
        this.availabilityData = data;
        this.available = Boolean(data?.available);
        this.canCreateSession = Boolean(data?.can_create_session ?? data?.available ?? true);
        this.availabilityReason = data?.reason || '';
      } catch (error) {
        this.available = false;
        this.canCreateSession = true;
        this.availabilityReason = 'availability_failed';
      }
    },
    openPanel() {
      this.panelOpen = true;
      this.recordPageVisit('open_chat');
      this.enableNotificationAudio();
      this.enableBrowserNotifications();
      if (this.sessionToken) {
        this.loadMessages();
        this.subscribeSessionChannel();
      }
    },
    closePanel() {
      this.stopOwnTyping({ force: true });
      this.panelOpen = false;
      this.recordPageVisit('minimize_chat');
    },
    subscribeAvailabilityChannel() {
      const companyId = this.companyId ? String(this.companyId) : '';
      if (!companyId) {
        this.unsubscribeAvailabilityChannel();
        return;
      }
      if (this.availabilitySubscribedCompany === companyId) return;

      this.unsubscribeAvailabilityChannel();
      const echo = this.createPublicEcho();
      if (!echo) return;

      this.availabilitySubscribedCompany = companyId;
      echo.channel(`booking-chat.availability.${companyId}`)
        .listen('.BookingChatAvailabilityUpdated', () => this.refreshAvailability());
    },
    unsubscribeAvailabilityChannel() {
      if (!this.echo || !this.availabilitySubscribedCompany) {
        this.availabilitySubscribedCompany = '';
        return;
      }

      try {
        this.echo.leave(`booking-chat.availability.${this.availabilitySubscribedCompany}`);
      } catch (error) {
        // Leaving can be a no-op if the socket already disconnected.
      } finally {
        this.availabilitySubscribedCompany = '';
      }
    },
    createPublicEcho() {
      if (this.echo) return this.echo;

      const key = process.env.VUE_APP_PUSHER_APP_KEY || 'traveldesk-app-key';
      const host = process.env.VUE_APP_PUSHER_HOST || 'socket.traveldesk.mx';
      const cluster = process.env.VUE_APP_PUSHER_CLUSTER || 'mt1';

      if (!key || !host) return null;

      this.pusher = new Pusher(key, {
        cluster,
        wsHost: host,
        wsPort: 80,
        wssPort: 443,
        forceTLS: true,
        enabledTransports: ['wss', 'ws'],
        enableStats: false,
      });

      this.echo = new Echo({
        broadcaster: 'pusher',
        client: this.pusher,
      });

      return this.echo;
    },
    subscribeSessionChannel() {
      if (!this.sessionToken || this.subscribedToken === this.sessionToken) return;
      const echo = this.createPublicEcho();
      if (!echo) return;

      this.unsubscribeSessionChannel();
      this.subscribedToken = this.sessionToken;
      echo.channel(`booking-chat.session.${this.sessionToken}`)
        .listen('.BookingChatUpdated', (event) => this.applyRealtimeEvent(event))
        .listen('.BookingChatTyping', (event) => this.applyTypingEvent(event));
    },
    unsubscribeSessionChannel() {
      if (!this.echo || !this.subscribedToken) return;
      try {
        this.echo.leave(`booking-chat.session.${this.subscribedToken}`);
      } catch (error) {
        // Safe fallback: leaving a public channel can be a no-op depending on the client state.
      } finally {
        this.subscribedToken = '';
      }
    },
    disconnectEcho() {
      try { this.echo?.disconnect(); } catch (error) { /* noop */ }
      try { this.pusher?.disconnect(); } catch (error) { /* noop */ }
      this.echo = null;
      this.pusher = null;
    },
    async loadMessages() {
      if (!this.sessionToken) return;
      this.loadingMessages = this.messages.length === 0;

      try {
        const response = await fetch(this.apiUrl(`public/booking-chat/sessions/${this.sessionToken}/messages?limit=80`));
        if (response.status === 404) {
          this.persistSessionToken('');
          this.sessionToken = '';
          this.messages = [];
          return;
        }

        const payload = await response.json();
        const session = payload?.data?.session || null;
        this.messages = payload?.data?.messages || [];
        this.applySessionState(session);
        this.$nextTick(this.scrollMessages);
      } catch (error) {
        // WebSocket updates will continue; user-facing errors are shown only on send failures.
      } finally {
        this.loadingMessages = false;
      }
    },
    async sendMessage() {
      if ((!this.draft && !this.attachment) || this.sending || this.sessionClosed) return;

      this.sending = true;
      this.errorMessage = '';
      const body = this.draft || (this.attachment ? this.copy.imageMessage : '');
      const fileToSend = this.attachment;
      const previewUrl = this.attachmentPreviewUrl;

      try {
        if (!this.sessionToken) {
          await this.createSession(body, fileToSend);
        } else {
          await this.sendSessionMessage(body, fileToSend, previewUrl);
        }

        this.draft = '';
        this.attachment = null;
        this.attachmentPreviewUrl = '';
        this.sendTyping(false);
        this.subscribeSessionChannel();
        this.$nextTick(this.scrollMessages);
      } catch (error) {
        this.errorMessage = this.copy.error;
        this.attachment = fileToSend;
        this.attachmentPreviewUrl = previewUrl;
      } finally {
        this.sending = false;
      }
    },
    async createSession(body, attachment = null) {
      const metadata = this.visitorMetadata();
      const headers = { Accept: 'application/json' };
      let requestBody = null;

      if (attachment) {
        requestBody = new FormData();
        requestBody.append('company_id', this.companyId);
        requestBody.append('industry', this.industry);
        requestBody.append('body', body || this.copy.imageMessage);
        requestBody.append('guest_name', this.guest.name || '');
        requestBody.append('guest_phone', this.guest.phone || '');
        requestBody.append('guest_email', this.guest.email || '');
        requestBody.append('source_url', window.location.href);
        requestBody.append('metadata', JSON.stringify(metadata));
        requestBody.append('visitor_funnel', JSON.stringify(this.visitedPages));
        requestBody.append('attachment', attachment);
      } else {
        headers['Content-Type'] = 'application/json';
        requestBody = JSON.stringify({
          company_id: this.companyId,
          industry: this.industry,
          body,
          guest_name: this.guest.name || null,
          guest_phone: this.guest.phone || null,
          guest_email: this.guest.email || null,
          source_url: window.location.href,
          metadata,
          visitor_funnel: this.visitedPages,
        });
      }

      const response = await fetch(this.apiUrl('public/booking-chat/sessions'), {
        method: 'POST',
        headers,
        body: requestBody,
      });

      if (!response.ok) throw new Error('session_failed');

      const payload = await response.json();
      const session = payload?.data?.session || null;
      this.sessionToken = session?.public_token || '';
      this.persistSessionToken(this.sessionToken);
      this.messages = payload?.data?.messages || [];
      this.applySessionState(session);
    },
    async sendSessionMessage(body, attachment = null, previewUrl = '') {
      const clientTag = `guest_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const localMessage = {
        id: `local_${Date.now()}`,
        _local: true,
        _status: 'sending',
        booking_chat_session_id: null,
        sender_type: 'guest',
        sender_user_id: null,
        body,
        meta: {
          client_tag: clientTag,
          attachment: attachment ? {
            name: attachment.name,
            mime: attachment.type,
            size: attachment.size,
            preview_url: previewUrl,
          } : null,
        },
        created_at: new Date().toISOString(),
        sender: null,
      };

      this.messages.push(localMessage);
      this.$nextTick(this.scrollMessages);

      const meta = {
        route_name: this.$route.name || null,
        route_path: this.$route.fullPath || null,
        client_tag: clientTag,
      };
      const headers = { Accept: 'application/json' };
      let requestBody = null;

      if (attachment) {
        requestBody = new FormData();
        requestBody.append('body', body || this.copy.imageMessage);
        requestBody.append('meta', JSON.stringify(meta));
        requestBody.append('visitor_funnel', JSON.stringify(this.visitedPages));
        requestBody.append('attachment', attachment);
      } else {
        headers['Content-Type'] = 'application/json';
        requestBody = JSON.stringify({ body, meta, visitor_funnel: this.visitedPages });
      }

      const response = await fetch(this.apiUrl(`public/booking-chat/sessions/${this.sessionToken}/messages`), {
        method: 'POST',
        headers,
        body: requestBody,
      });

      if (!response.ok) {
        const index = this.messages.findIndex((message) => message.id === localMessage.id);
        if (index !== -1) this.messages.splice(index, 1, { ...localMessage, _status: 'failed' });
        throw new Error('message_failed');
      }

      const payload = await response.json();
      if (payload?.data) this.appendMessageOnce(payload.data);
    },
    eventKey(event) {
      if (event?.message?.id) return `${event.action || 'message'}:${event.message.id}`;
      const session = event?.session || null;
      return `${event?.action || 'session'}:${session?.id || this.sessionToken}:${session?.last_message_at || session?.closed_at || session?.rated_at || 'state'}`;
    },
    rememberEvent(event) {
      const key = this.eventKey(event);
      if (this.handledEventKeys.has(key)) return true;
      this.handledEventKeys.add(key);
      if (this.handledEventKeys.size > 250) {
        this.handledEventKeys = new Set(Array.from(this.handledEventKeys).slice(-160));
      }
      return false;
    },
    applyRealtimeEvent(event) {
      if (this.rememberEvent(event)) return;
      const session = event?.session || null;
      const message = event?.message || null;
      if (session) this.applySessionState(session);

      if (message) {
        this.appendMessageOnce(message);
        if (message.sender_type === 'agent') this.notifyIncoming(message);
        this.$nextTick(this.scrollMessages);
      }
    },
    applySessionState(session) {
      if (!session) return;
      this.sessionStatus = session.status || this.sessionStatus || 'open';
      this.rating = Number(session.rating || this.rating || 0);
      this.ratingComment = session.rating_comment || this.ratingComment || '';
      this.ratingSubmitted = Boolean(session.rated_at || session.rating);
    },
    ratingValueFromPointer(event, star) {
      const rect = event?.currentTarget?.getBoundingClientRect?.();
      if (!rect?.width) return star;
      const offset = Number(event.clientX || 0) - rect.left;
      return offset <= rect.width / 2 ? star - 0.5 : star;
    },
    previewRating(event, star) {
      this.hoverRating = this.ratingValueFromPointer(event, star);
    },
    setRatingFromPointer(event, star) {
      this.rating = this.ratingValueFromPointer(event, star);
      this.hoverRating = this.rating;
    },
    starFill(star) {
      const value = this.displayRating;
      if (value >= star) return 100;
      if (value >= star - 0.5) return 50;
      return 0;
    },
    starLabel(star) {
      const half = star - 0.5;
      return `${half} / 5 or ${star} / 5`;
    },
    applyTypingEvent(event) {
      if (event?.sender_type !== 'agent') return;
      if (event?.public_token && event.public_token !== this.sessionToken) return;
      window.clearTimeout(this.agentTypingTimer);
      this.agentTyping = Boolean(event?.is_typing);
      if (this.agentTyping) {
        this.$nextTick(this.scrollMessages);
        this.agentTypingTimer = window.setTimeout(() => {
          this.agentTyping = false;
        }, 2600);
      }
    },
    appendMessageOnce(message) {
      if (this.messages.some((item) => Number(item.id) === Number(message.id))) return;

      const serverTag = this.messageMeta(message)?.client_tag || null;
      const localIndex = this.messages.findIndex((item) => {
        if (!item._local) return false;
        const localTag = this.messageMeta(item)?.client_tag || null;
        if (serverTag && localTag === serverTag) return true;
        return item.body === message.body && item.sender_type === message.sender_type;
      });

      if (localIndex !== -1) {
        this.messages.splice(localIndex, 1, message);
        return;
      }

      this.messages.push(message);
    },
    handleTypingInput() {
      if (!this.sessionToken || this.sessionClosed) return;
      window.clearTimeout(this.typingStopTimer);

      if (!this.shouldSendTyping()) {
        this.stopOwnTyping();
        return;
      }

      this.sendTyping(true, { force: true });
      this.typingStopTimer = window.setTimeout(() => this.handleTypingInput(), 1800);
    },
    handleTypingFocus() {
      this.typingComposerFocused = true;
      this.handleTypingInput();
    },
    handleTypingBlur() {
      this.typingComposerFocused = false;
      this.stopOwnTyping({ force: true });
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.stopOwnTyping({ force: true });
        return;
      }

      this.handleTypingInput();
    },
    shouldSendTyping() {
      return Boolean(
        this.panelOpen
        && this.typingComposerFocused
        && !document.hidden
        && String(this.draft || '').trim()
      );
    },
    stopOwnTyping({ force = false } = {}) {
      window.clearTimeout(this.typingStopTimer);
      if (this.typingState || force) this.sendTyping(false, { force: true });
    },
    async sendTyping(isTyping, { force = false } = {}) {
      if (!this.sessionToken) return;
      if (this.sessionClosed && isTyping) return;
      if (this.typingState === Boolean(isTyping) && !force) return;
      this.typingState = Boolean(isTyping);

      try {
        await fetch(this.apiUrl(`public/booking-chat/sessions/${this.sessionToken}/typing`), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_typing: this.typingState }),
        });
      } catch (error) {
        // Typing indicators are non-blocking.
      }
    },
    async closeConversation() {
      if (!this.sessionToken || this.sessionClosed) return;
      this.sendTyping(false, { force: true });
      this.ratingError = '';
      const previousStatus = this.sessionStatus;
      this.sessionStatus = 'closed';

      try {
        const response = await fetch(this.apiUrl(`public/booking-chat/sessions/${this.sessionToken}/close`), {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(await this.responseMessage(response, this.copy.closeError));
        }
        const payload = await response.json();
        this.applySessionState(payload?.data || null);
      } catch (error) {
        this.sessionStatus = previousStatus;
        this.errorMessage = error?.message || this.copy.error;
      }
    },
    async submitRating() {
      if (!this.sessionToken || !this.rating || this.ratingSending) return;
      this.ratingSending = true;
      this.ratingError = '';
      this.errorMessage = '';

      try {
        const response = await fetch(this.apiUrl(`public/booking-chat/sessions/${this.sessionToken}/rating`), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rating: this.rating,
            rating_comment: this.ratingComment || null,
          }),
        });
        if (!response.ok) {
          throw new Error(await this.responseMessage(response, this.copy.ratingError));
        }
        const payload = await response.json();
        this.applySessionState(payload?.data || null);
        this.ratingSubmitted = true;
      } catch (error) {
        this.ratingError = error?.message || this.copy.ratingError;
      } finally {
        this.ratingSending = false;
      }
    },
    startNewConversation() {
      this.stopOwnTyping({ force: true });
      this.unsubscribeSessionChannel();
      this.persistSessionToken('');
      this.sessionToken = '';
      this.sessionStatus = 'open';
      this.messages = [];
      this.draft = '';
      this.rating = 0;
      this.hoverRating = 0;
      this.ratingComment = '';
      this.ratingError = '';
      this.ratingSubmitted = false;
      this.errorMessage = '';
      this.agentTyping = false;
      this.typingState = false;
      this.handledEventKeys = new Set();
      this.notifiedMessageIds = new Set();
      this.stopTypingTimers();
      this.clearAttachment();
      this.recordPageVisit('new_chat');
    },
    stopTypingTimers() {
      window.clearTimeout(this.agentTypingTimer);
      window.clearTimeout(this.typingStopTimer);
      this.agentTyping = false;
      this.typingComposerFocused = false;
    },
    openAttachmentPicker() {
      this.$refs.attachmentInput?.click();
    },
    handleFilePicked(event) {
      const file = event?.target?.files?.[0] || null;
      if (file) this.selectAttachment(file);
      if (event?.target) event.target.value = '';
    },
    handleDrop(event) {
      this.dragActive = false;
      const file = Array.from(event?.dataTransfer?.files || []).find((item) => item.type?.startsWith('image/'));
      if (file) this.selectAttachment(file);
    },
    handlePaste(event) {
      const item = Array.from(event?.clipboardData?.items || []).find((entry) => entry.type?.startsWith('image/'));
      const file = item?.getAsFile?.() || null;
      if (file) {
        event.preventDefault();
        this.selectAttachment(file);
      }
    },
    selectAttachment(file) {
      this.attachmentError = '';
      if (!file?.type?.startsWith('image/')) {
        this.attachmentError = this.copy.imageOnly;
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.attachmentError = this.copy.imageTooLarge;
        return;
      }

      this.clearAttachment();
      this.attachment = file;
      this.attachmentPreviewUrl = URL.createObjectURL(file);
    },
    clearAttachment() {
      if (this.attachmentPreviewUrl) URL.revokeObjectURL(this.attachmentPreviewUrl);
      this.attachment = null;
      this.attachmentPreviewUrl = '';
    },
    openImage(attachment) {
      const url = attachment?.url || attachment?.preview_url || '';
      if (!url) return;
      this.lightboxImage = { url, name: attachment?.name || 'Attachment' };
    },
    closeLightbox() {
      this.lightboxImage = null;
    },
    appendEmoji(emoji) {
      this.draft = `${this.draft || ''}${emoji}`;
      this.showEmojiPicker = false;
      this.$nextTick(() => this.$el.querySelector('textarea')?.focus());
    },
    messageMeta(message) {
      const meta = message?.meta || {};
      if (typeof meta === 'string') {
        try { return JSON.parse(meta); } catch (error) { return {}; }
      }
      return meta || {};
    },
    messageAttachment(message) {
      return this.messageMeta(message)?.attachment || null;
    },
    shouldShowBody(message) {
      if (!message?.body) return false;
      if (this.messageAttachment(message) && ['Image', 'Imagen'].includes(message.body)) return false;
      return true;
    },
    senderLabel(message) {
      if (message.sender_type === 'guest') return this.copy.guest;
      const sender = message.sender || {};
      const name = `${sender.user_fname || ''} ${sender.user_lname || ''}`.trim();
      return name || this.copy.agent;
    },
    messageAvatar(message) {
      if (message.sender_type === 'guest') return '';
      const image = message.sender?.user_image || '';
      if (!image || image.includes('user-default.png')) return '';
      return image;
    },
    messageInitials(message) {
      const name = this.senderLabel(message);
      const parts = String(name || 'Agent').trim().split(/\s+/).filter(Boolean);
      return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || 'A';
    },
    notifyIncoming(message) {
      const messageId = message?.id ? String(message.id) : '';
      if (messageId) {
        if (this.notifiedMessageIds.has(messageId)) return;
        this.notifiedMessageIds.add(messageId);
        if (this.notifiedMessageIds.size > 250) {
          this.notifiedMessageIds = new Set(Array.from(this.notifiedMessageIds).slice(-160));
        }
      }

      this.playNotificationSound();

      if (this.panelOpen && !document.hidden) return;

      try {
        if (!("Notification" in window)) return;
        const body = String(message?.body || this.copy.imageMessage).slice(0, 120);
        if (Notification.permission === 'granted') new Notification(this.copy.browserTitle, { body });
      } catch (error) {
        // Browser notifications are optional.
      }
    },
    initNotificationAudio() {
      if (this.notificationAudio || typeof Audio === 'undefined') return;
      this.notificationAudio = new Audio('/new-notif.mp3');
      this.notificationAudio.volume = 0.35;
      this.notificationAudio.preload = 'auto';
    },
    enableNotificationAudio() {
      this.initNotificationAudio();
      if (!this.notificationAudio || this.notificationAudioUnlocked) return;

      try {
        this.notificationAudio.muted = true;
        const playPromise = this.notificationAudio.play();
        if (playPromise?.then) {
          playPromise
            .then(() => {
              this.notificationAudio.pause();
              this.notificationAudio.currentTime = 0;
              this.notificationAudio.muted = false;
              this.notificationAudioUnlocked = true;
            })
            .catch(() => {
              this.notificationAudio.muted = false;
            });
        } else {
          this.notificationAudio.muted = false;
          this.notificationAudioUnlocked = true;
        }
      } catch (error) {
        this.notificationAudio.muted = false;
      }
    },
    playNotificationSound() {
      try {
        this.initNotificationAudio();
        const audio = this.notificationAudio || new Audio('/new-notif.mp3');
        audio.volume = 0.35;
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } catch (error) {
        // Browser can block sound until the user interacts.
      }
    },
    enableBrowserNotifications() {
      try {
        if (("Notification" in window) && Notification.permission === 'default') {
          Notification.requestPermission();
        }
      } catch (error) {
        // Optional browser capability.
      }
    },
    bookingStepLabels() {
      return {
        transportation: {
          1: { key: 'trip', en: 'Trip', es: 'Viaje' },
          2: { key: 'vehicle', en: 'Vehicle', es: 'Vehiculo' },
          3: { key: 'extras', en: 'Extras', es: 'Extras' },
          4: { key: 'checkout', en: 'Checkout', es: 'Checkout' },
          5: { key: 'confirmation', en: 'Confirmation', es: 'Confirmacion' },
        },
        tours: {
          1: { key: 'activities', en: 'Activities', es: 'Actividades' },
          2: { key: 'booking', en: 'Booking', es: 'Reserva' },
          3: { key: 'cart', en: 'Cart', es: 'Carrito' },
          4: { key: 'checkout', en: 'Checkout', es: 'Checkout' },
          5: { key: 'confirmation', en: 'Confirmation', es: 'Confirmacion' },
        },
        restaurant: {
          1: { key: 'date_and_guests', en: 'Date and guests', es: 'Fecha y personas' },
          2: { key: 'contact', en: 'Contact', es: 'Contacto' },
          3: { key: 'confirmation', en: 'Confirmation', es: 'Confirmacion' },
        },
      };
    },
    currentBookingStep() {
      const labels = this.bookingStepLabels()[this.industry] || {};
      const maxStep = Object.keys(labels).length || 1;
      const confirmationRoutes = [
        'public-reservation-confirmation',
        'public-tour-reservation-confirmation',
        'public-restaurant-reservation-confirmation',
      ];
      const stepNumber = confirmationRoutes.includes(this.$route.name)
        ? maxStep
        : (this.industry === 'restaurant'
          ? this.restaurantStepNumber()
          : Math.max(1, Math.min(Number(this.currentStep || 1), maxStep)));
      const step = labels[stepNumber] || labels[1] || { key: 'booking_step', en: 'Booking step', es: 'Paso de reserva' };
      return {
        key: step.key || `${this.industry || 'booking'}:${stepNumber}`,
        number: stepNumber,
        industry: this.industry || 'booking',
        label: step[this.language] || step.en,
        label_en: step.en,
        label_es: step.es,
      };
    },
    restaurantStepNumber() {
      if (document.querySelector('.restaurant-page .confirmation')) return 3;
      if (document.querySelector('.restaurant-page .hold-card, .restaurant-page .selected-slot-card')) return 2;
      return 1;
    },
    trackCurrentBookingStep() {
      if (!this.shouldRender) return;
      this.recordPageVisit('booking_step');
    },
    recordPageVisit(action = 'view') {
      if (!this.shouldRender) return;
      const bookingStep = this.currentBookingStep();
      const item = {
        action,
        path: this.$route.fullPath || window.location.pathname,
        route_name: this.$route.name || null,
        title: bookingStep.label,
        booking_step: bookingStep,
        industry: this.industry || null,
        at: new Date().toISOString(),
      };
      const last = this.visitedPages[this.visitedPages.length - 1];
      if (last?.booking_step?.key === item.booking_step.key) return;
      this.visitedPages.push(item);
      this.visitedPages = this.visitedPages.slice(-40);
      this.trackBookingFunnelVisit(item);
    },
    ensureFunnelSessionKey() {
      if (!this.funnelStorageKey) return '';
      if (this.funnelSessionKey) return this.funnelSessionKey;

      try {
        const existing = window.sessionStorage.getItem(this.funnelStorageKey);
        if (existing) {
          this.funnelSessionKey = existing;
          return existing;
        }
      } catch (error) {
        // Private browsing can block sessionStorage.
      }

      const key = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `bf_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      this.funnelSessionKey = key;
      try {
        window.sessionStorage.setItem(this.funnelStorageKey, key);
      } catch (error) {
        // Tracking still works for the current component instance.
      }

      return key;
    },
    trackBookingFunnelVisit(item) {
      if (!this.shouldRender || !item?.booking_step) return;

      const sessionKey = this.ensureFunnelSessionKey();
      if (!sessionKey) return;

      const signature = `${sessionKey}|${item.booking_step.key}|${item.path}|${item.action}`;
      if (signature === this.lastFunnelVisitSignature) return;
      this.lastFunnelVisitSignature = signature;

      const routePath = this.$route.fullPath || window.location.pathname || '';
      const payload = {
        company_id: this.companyId,
        industry: this.industry,
        session_key: sessionKey,
        action: item.action || 'view',
        route_name: this.$route.name || null,
        route_path: routePath,
        path: routePath,
        source_url: window.location?.pathname || routePath,
        referrer: document.referrer || null,
        locale: this.language,
        at: item.at,
        booking_step: item.booking_step,
        visitor_funnel: this.visitedPages,
      };

      const url = this.apiUrl('public/booking-funnel/visits');
      const body = JSON.stringify(payload);

      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon(url, blob)) return;
        }

        fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch (error) {
        // Funnel tracking must never interrupt the booking flow.
      }
    },
    visitorMetadata() {
      return {
        route_name: this.$route.name || null,
        route_path: this.$route.fullPath || null,
        language: this.language,
        chat_started_at: new Date().toISOString(),
        visitor_started_at: this.visitorStartedAt,
        funnel_session_key: this.ensureFunnelSessionKey(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        user_agent: window.navigator?.userAgent || null,
        screen: {
          width: window.screen?.width || null,
          height: window.screen?.height || null,
        },
        availability: {
          reason: this.availabilityReason,
          active_agents_count: this.availabilityData?.active_agents_count || 0,
          online_agents_count: this.availabilityData?.online_agents_count || 0,
          available: this.available,
        },
        visitor_funnel: this.visitedPages,
      };
    },
    formatTime(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat(this.language === 'es' ? 'es-MX' : 'en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
    },
    scrollMessages() {
      const box = this.$refs.messagesBox;
      if (box) box.scrollTop = box.scrollHeight;
    },
    hexToRgba(hex, alpha) {
      const clean = String(hex || '#2d1b78').replace('#', '');
      const normalized = clean.length === 3 ? clean.split('').map((char) => char + char).join('') : clean.slice(0, 6);
      const value = parseInt(normalized, 16);
      if (Number.isNaN(value)) return `rgba(45, 27, 120, ${alpha})`;
      const r = (value >> 16) & 255;
      const g = (value >> 8) & 255;
      const b = value & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
.td-booking-chat {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 8000;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  text-align: left;
}

.td-booking-chat__launcher {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 46px;
  padding: 0 18px;
  border: 1px solid;
  border-radius: 999px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, .28), 0 0 0 1px rgba(255, 255, 255, .42) inset;
  font-weight: 850;
}

.td-booking-chat__dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
}

.td-booking-chat__panel {
  width: min(376px, calc(100vw - 28px));
  overflow: hidden;
  border: 1px solid #dde5f2;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(23, 32, 51, .24);
}

.td-booking-chat__panel.dragging {
  box-shadow: 0 24px 60px rgba(23, 32, 51, .24), 0 0 0 3px rgba(45, 27, 120, .18);
}

.td-booking-chat__header {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 16px 13px;
  border-top: 5px solid;
  border-bottom: 1px solid #e8edf5;
}

.td-booking-chat__header strong {
  display: block;
  color: #111827;
  font-size: 16px;
}

.td-booking-chat__header span {
  display: block;
  margin-top: 2px;
  color: #667085;
  font-size: 13px;
}

.td-booking-chat__close {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 999px;
  background: #f2f4f7;
  color: #344054;
  font-weight: 900;
}

.td-booking-chat__body {
  display: grid;
  gap: 12px;
  max-height: min(56vh, 430px);
  overflow: auto;
  padding: 14px 16px;
}

.td-booking-chat__intro {
  margin: 0;
  color: #566176;
  font-size: 14px;
}

.td-booking-chat label {
  display: grid;
  gap: 6px;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}

.td-booking-chat input,
.td-booking-chat textarea {
  width: 100%;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  padding: 10px 11px;
  color: #111827;
  font: inherit;
  font-weight: 500;
  resize: vertical;
}

.td-booking-chat__messages {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 180px;
  overflow-y: auto;
  padding-bottom: 6px;
}

.td-booking-chat__message-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 88%;
}

.td-booking-chat__message-row.guest {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.td-booking-chat__avatar {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  overflow: hidden;
  border-radius: 999px;
  background: #eef4ff;
  color: #175cd3;
  font-size: 11px;
  font-weight: 850;
}

.td-booking-chat__avatar.guest {
  background: #ebe7ff;
  color: #250b6e;
}

.td-booking-chat__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.td-booking-chat__message {
  min-width: 0;
  border: 1px solid #e4e9f2;
  border-radius: 13px 13px 13px 4px;
  background: #f8fafc;
  padding: 9px 11px;
  color: #1d2939;
  font-size: 14px;
  text-align: left;
}

.td-booking-chat__message-row.guest .td-booking-chat__message {
  border-radius: 13px 13px 4px 13px;
  background: #efeaff;
}

.td-booking-chat__message-sender {
  margin-bottom: 3px;
  color: #475467;
  font-size: 12px;
  font-weight: 800;
}

.td-booking-chat__message-body {
  white-space: pre-wrap;
  word-break: break-word;
}

.td-booking-chat__message-attachment {
  display: block;
  overflow: hidden;
  width: min(240px, 62vw);
  margin-bottom: 7px;
  border: 1px solid #e4e9f2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 0;
  cursor: zoom-in;
}

.td-booking-chat__message-attachment img {
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: cover;
}

.td-booking-chat__typing {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: fit-content;
  border: 1px solid #e4e9f2;
  border-radius: 999px;
  background: #fff;
  color: #667085;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 8px 22px rgba(15, 23, 42, .08);
}

.td-booking-chat__typing span {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #12b76a;
  animation: td-booking-chat-pulse 1s infinite ease-in-out;
}

.td-booking-chat__message small {
  display: block;
  margin-top: 5px;
  color: #667085;
  font-size: 11px;
}

.td-booking-chat__alert {
  border: 1px solid #f6c8c3;
  border-radius: 10px;
  background: #fff4f2;
  color: #b42318;
  padding: 9px 10px;
  font-size: 13px;
  font-weight: 700;
}

.td-booking-chat__footer {
  display: grid;
  gap: 9px;
  padding: 12px 16px 16px;
  border-top: 1px solid #e8edf5;
}

.td-booking-chat__file {
  display: none;
}

.td-booking-chat__actions {
  display: grid;
  position: relative;
  grid-template-columns: 44px 44px minmax(0, 1fr);
  gap: 8px;
}

.td-booking-chat__attach,
.td-booking-chat__send {
  min-height: 42px;
  border: 1px solid;
  border-radius: 12px;
  font-weight: 850;
}

.td-booking-chat__attach {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  border-color: #d0d5dd;
  border-radius: 999px;
  background: #fff;
  color: #344054;
  padding: 0;
  font-size: 15px;
}

.td-booking-chat__attach:hover {
  background: #f8fafc;
  border-color: #b8c0cc;
}

.td-booking-chat__send:disabled {
  cursor: not-allowed;
  opacity: .56;
}

.td-booking-chat__attachment-preview {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 9px;
  border: 1px solid #e1e7f0;
  border-radius: 12px;
  background: #f8fafc;
  padding: 7px;
}

.td-booking-chat__attachment-preview img {
  width: 46px;
  height: 38px;
  border-radius: 8px;
  object-fit: cover;
}

.td-booking-chat__attachment-preview span {
  overflow: hidden;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-booking-chat__attachment-preview button {
  width: 28px;
  height: 28px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  background: #fff;
  color: #344054;
  font-weight: 900;
}

.td-booking-chat__attachment-error {
  color: #b42318;
  font-size: 12px;
  font-weight: 800;
}

.td-booking-chat__skeletons {
  display: contents;
}

.td-booking-chat__skeletons span {
  display: block;
  width: 72%;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(90deg, #eef1f7, #f8fafc, #eef1f7);
  background-size: 220% 100%;
  animation: td-booking-chat-shimmer 1.2s infinite linear;
}

.td-booking-chat__skeletons span.guest {
  align-self: flex-end;
}


.td-booking-chat__offline,
.td-booking-chat__thanks {
  border: 1px solid #fedf89;
  border-radius: 12px;
  background: #fffbeb;
  color: #7a2e0e;
  padding: 10px 11px;
  font-size: 13px;
  font-weight: 750;
}

.td-booking-chat__thanks {
  display: grid;
  gap: 3px;
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.td-booking-chat__closed {
  display: grid;
  gap: 1px;
  border: 0;
  background: transparent;
  color: #667085;
  padding: 0 2px;
  font-size: 12px;
  font-weight: 600;
}

.td-booking-chat__closed strong {
  color: #475467;
  font-size: 12px;
}

.td-booking-chat__closed span {
  font-size: 12px;
}

.td-booking-chat__end,
.td-booking-chat__new {
  justify-self: start;
  min-height: 36px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  background: #fff;
  color: #475467;
  padding: 0 13px;
  font-size: 12px;
  font-weight: 850;
}

.td-booking-chat__end:hover,
.td-booking-chat__new:hover {
  border-color: #98a2b3;
  background: #f8fafc;
  color: #1d2939;
}

.td-booking-chat__emoji-picker {
  position: absolute;
  left: 48px;
  bottom: 50px;
  z-index: 5;
  display: grid;
  grid-template-columns: repeat(6, 32px);
  gap: 5px;
  border: 1px solid #d5dbe7;
  border-radius: 12px;
  background: #fff;
  padding: 8px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, .16);
}

.td-booking-chat__emoji-picker button {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 17px;
}

.td-booking-chat__rating {
  display: grid;
  gap: 9px;
  border: 1px solid #e4e9f2;
  border-radius: 14px;
  background: #fff;
  padding: 11px;
}

.td-booking-chat__stars {
  display: flex;
  gap: 4px;
}

.td-booking-chat__star {
  position: relative;
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  color: #d0d5dd;
  cursor: pointer;
  font-size: 26px;
  line-height: 1;
  padding: 0;
}

.td-booking-chat__star-base,
.td-booking-chat__star-fill {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
}

.td-booking-chat__star-fill {
  width: 100%;
  color: #f59e0b;
  clip-path: inset(0 calc(100% - var(--star-fill, 0%)) 0 0);
  white-space: nowrap;
}

.td-booking-chat__star:focus-visible {
  outline: 2px solid rgba(45, 27, 120, .3);
  outline-offset: 2px;
}

.td-booking-chat__lightbox {
  position: fixed;
  inset: 0;
  z-index: 100001;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, .72);
  padding: 24px;
}

.td-booking-chat__lightbox img {
  max-width: min(920px, 94vw);
  max-height: 86vh;
  border-radius: 14px;
  background: #fff;
  object-fit: contain;
  box-shadow: 0 24px 80px rgba(0, 0, 0, .34);
}

.td-booking-chat__lightbox-close {
  position: absolute;
  top: 18px;
  right: 18px;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #101828;
  font-weight: 900;
}
@keyframes td-booking-chat-shimmer {
  to { background-position: -220% 0; }
}

@keyframes td-booking-chat-pulse {
  0%, 100% { opacity: .35; transform: scale(.92); }
  50% { opacity: 1; transform: scale(1); }
}

@media (max-width: 560px) {
  .td-booking-chat {
    right: 12px;
    bottom: 12px;
  }

  .td-booking-chat__panel {
    width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
  }

  .td-booking-chat__body {
    max-height: 58dvh;
  }
}
</style>